/**
 * Мульти-таймфрейм: старший ряд решает сторону, младший — момент входа.
 *
 * Условие на старшем таймфрейме берёт последний **закрытый** бар того
 * ряда: взять текущий значило бы заглянуть в будущее. Рекомендуемый
 * таймфрейм у идей раздела — младший: на нём и открывают график.
 */
import { above, close, ema, happened, ind, level, mirror, number, onFrame, rsi, rvol, spec, structureTrend, swingParams, weight, } from './kit.js';
export const MULTI_TF_TEMPLATES = [
    {
        id: 'multi-tf-trend',
        name: 'Тренд 4ч, вход на своём',
        icon: '⏱',
        category: 'multi-tf',
        popular: true,
        hint: 'Сторону решает четырёхчасовой ряд, момент входа — тот, что открыт на экране.',
        steps: ['Тренд на 4ч в нашу сторону', 'Цена пересекла EMA 20 на своём ряду', 'Объём'],
        keywords: ['мульти', 'таймфрейм', 'старший', '4h'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Сначала смотрим на 4 часа: цена выше EMA 200 — ищем только покупки. Потом на своём графике ждём пересечения EMA 20.',
            why: 'Входы по старшему тренду получают «ветер в спину», а младший таймфрейм даёт точную цену входа.',
            fails: 'Когда 4-часовой тренд разворачивается, младшие входы некоторое время ещё идут против нового движения.',
            tip: 'Самый простой способ перестать торговать против рынка.',
        },
        build: (o) => spec(o, 'Тренд 4ч, вход на своём', mirror((side) => ({
            context: [onFrame(side.beyond(close, ema(200)), '4H')],
            trigger: side.cross(close, ema(20)),
            confirmations: [weight('c1', above(rvol, number(1.2)), 100)],
        })), 0),
    },
    {
        id: 'multi-tf-structure',
        name: 'Структура часа, слом на своём',
        icon: '⏱',
        category: 'multi-tf',
        hint: 'Часовая структура задаёт сторону, вход — по слому характера на младшем ряду.',
        steps: ['Структура часа в нашу сторону', 'Слом характера на своём ряду', 'Объём'],
        keywords: ['мульти', 'структура', '1h', 'choch'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'На часе структура растущая; на 15 минутах был откат, и теперь его характер сломался обратно вверх.',
            why: 'Слом характера младшего ряда по ходу старшего — конец отката, а не начало разворота.',
            fails: 'Когда часовая структура сама вот-вот сломается — входы оказываются поздними.',
            tip: 'На часовом графике идея теряет смысл: структура «часа» совпадёт с вашей.',
        },
        build: (o) => spec(o, 'Структура часа, слом на своём', mirror((side) => ({
            context: [onFrame(side.beyond(structureTrend(), number(0)), '1H')],
            trigger: happened(ind('choch', swingParams, side.way)),
            confirmations: [weight('c1', above(rvol, number(1.2)), 100)],
        })), 0),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'daily-hourly',
        name: 'Дневной тренд, часовой вход',
        icon: '🗓',
        category: 'multi-tf',
        hint: 'На дне цена выше EMA 50 — только покупки; на часе ждём отката и возврата выше EMA 20.',
        steps: ['На дне цена по нужную сторону от EMA 50', 'На часе цена вернулась за EMA 20', 'Объём'],
        keywords: ['дневной', 'часовой', 'мульти', 'тренд'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'День задаёт направление на недели вперёд, час — точку входа на откате.',
            why: 'Дневной тренд медленный и устойчивый; вход на часовом откате даёт хорошую цену внутри него.',
            fails: 'В дни, когда дневная свеча разворачивается, часовые входы идут против неё.',
            tip: 'Хороший первый бот: сделок немного, и каждая понятна.',
        },
        build: (o) => spec(o, 'Дневной тренд, часовой вход', mirror((side) => ({
            context: [onFrame(side.beyond(close, ema(50)), '1D')],
            trigger: side.cross(close, ema(20)),
            confirmations: [weight('c1', above(rvol, number(1.1)), 50), weight('c2', side.beyond(ema(20), ema(50)), 50)],
        })), 0),
    },
    {
        id: 'rsi4h-15m',
        name: 'RSI 4ч + вход на 15 минутах',
        icon: '⏱',
        category: 'multi-tf',
        hint: 'На 4 часах импульс на нашей стороне (RSI за 50), на 15 минутах RSI выходит из отката.',
        steps: ['RSI на 4ч по нужную сторону от 50', 'RSI на своём ряду вышел из зоны отката', 'Объём'],
        keywords: ['rsi', 'мульти', '4h', '15m', 'импульс'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Старший RSI показывает, чей сейчас импульс; младший — когда откат против него закончился.',
            why: 'Отбор по старшему импульсу убирает большую часть сигналов младшего RSI против рынка.',
            fails: 'Когда 4-часовой RSI около 50 — импульса нет, и фильтр мало что даёт.',
            tip: 'Совпадение с «Тренд 4ч, вход на своём» — сильная связка.',
        },
        build: (o) => spec(o, 'RSI 4ч + вход на 15 минутах', mirror((side) => ({
            context: [onFrame(side.beyond(rsi, number(50)), '4H')],
            trigger: side.cross(rsi, number(level(side, 40))),
            confirmations: [weight('c1', above(rvol, number(1.1)), 50), weight('c2', side.beyond(close, ema(50)), 50)],
        })), 0),
    },
    {
        id: 'structure4h-fvg',
        name: 'Структура 4ч + разрыв часа',
        icon: '🧩',
        category: 'multi-tf',
        hint: 'Четырёхчасовая структура по нашу сторону, на часе цена вернулась в разрыв.',
        steps: ['Структура 4ч в нашу сторону', 'Цена вернулась в разрыв на своём ряду', 'Объём'],
        keywords: ['fvg', 'структура', 'мульти', 'smart money', '4h'],
        passport: {
            level: 'pro',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Классика Smart Money в две ступени: старшая структура задаёт сторону, младший разрыв — точку входа.',
            why: 'Разрыв по направлению старшей структуры закрывают реже и отталкиваются от него сильнее.',
            fails: 'Когда 4-часовая структура на грани слома — разрывы часа не держат.',
            tip: 'Разрывы у начала импульса надёжнее, чем в его середине.',
        },
        build: (o) => spec(o, 'Структура 4ч + разрыв часа', mirror((side) => ({
            context: [onFrame(side.beyond(structureTrend(), number(0)), '4H')],
            trigger: happened(ind('fvg', { side: side.sign, size: 0.25 }, 'touched')),
            confirmations: [weight('c1', above(rvol, number(1.2)), 50), weight('c2', side.beyond(structureTrend(), number(0)), 50)],
        })), 0),
    },
    {
        id: 'all-agree',
        name: 'Все таймфреймы согласны',
        icon: '🎼',
        category: 'multi-tf',
        hint: 'День, 4 часа и час — все по одну сторону от EMA 50; вход на своём ряду по пересечению EMA 20.',
        steps: ['День, 4ч и час по нужную сторону от EMA 50', 'Цена пересекла EMA 20 на своём ряду', 'Объём'],
        keywords: ['мульти', 'согласие', 'все таймфреймы', 'тренд'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Входить только тогда, когда все старшие таймфреймы смотрят в одну сторону — рынок «в унисон».',
            why: 'Когда тренд виден на всех масштабах, у движения больше всего участников и меньше всего противников.',
            fails: 'Такие моменты случаются реже; и в самом конце тренда все таймфреймы тоже «согласны».',
            tip: 'Строгий фильтр: сигналов меньше, но почти все — по течению.',
        },
        build: (o) => spec(o, 'Все таймфреймы согласны', mirror((side) => ({
            context: [
                onFrame(side.beyond(close, ema(50)), '1D'),
                onFrame(side.beyond(close, ema(50)), '4H'),
                onFrame(side.beyond(close, ema(50)), '1H'),
            ],
            trigger: side.cross(close, ema(20)),
            confirmations: [weight('c1', above(rvol, number(1.1)), 100)],
        })), 0),
    },
];
//# sourceMappingURL=multi-tf.js.map